import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import {Button} from '../../../components/ui/Button';
import apiService from '../../../services/api';

const FrequentCollaborators = () => {
  const [showAll, setShowAll] = useState(false);
  const [collaborators, setCollaborators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCollaborators = async () => {
      try {
        const response = await apiService.request('/users/collaborators');
        if (response.success) {
          setCollaborators(response.data.collaborators || []);
        } else {
          throw new Error(response.message || 'Failed to fetch collaborators');
        }
      } catch (err) {
        setError(err.message);
        console.error('Error fetching collaborators:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchCollaborators();
  }, []);

  const displayedCollaborators = showAll ? collaborators : collaborators?.slice(0, 3);

  const getRelationshipBadge = (type) => {
    const config = {
      frequent: { color: 'text-green-700', bg: 'bg-green-100', label: 'Frequent', icon: 'Star' },
      regular: { color: 'text-blue-700', bg: 'bg-blue-100', label: 'Regular', icon: 'Users' },
      occasional: { color: 'text-gray-700', bg: 'bg-gray-100', label: 'Occasional', icon: 'User' }
    };
    
    const settings = config?.[type] || config?.occasional;
    
    return (
      <div className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${settings?.bg} ${settings?.color}`}>
        <Icon name={settings?.icon} size={12} className="mr-1" />
        {settings?.label}
      </div>
    );
  };

  const getTrustIndicator = (score) => {
    if (score >= 90) return { color: 'text-green-600', label: 'Excellent' };
    if (score >= 80) return { color: 'text-blue-600', label: 'Good' };
    if (score >= 70) return { color: 'text-yellow-600', label: 'Fair' };
    return { color: 'text-red-600', label: 'Poor' };
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Icon name="UserCheck" size={20} className="text-primary" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground">Frequent Collaborators</h3>
              <p className="text-sm text-muted-foreground">
                People you share expenses with regularly
              </p>
            </div>
          </div>
        </div>
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Icon name="UserCheck" size={20} className="text-primary" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground">Frequent Collaborators</h3>
              <p className="text-sm text-muted-foreground">
                People you share expenses with regularly
              </p>
            </div>
          </div>
        </div>
        <div className="text-center py-8 text-muted-foreground">
          <p>Unable to load collaborators</p>
          <p className="text-sm mt-1">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Icon name="UserCheck" size={20} className="text-primary" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Frequent Collaborators</h3>
            <p className="text-sm text-muted-foreground">
              People you share expenses with regularly
            </p>
          </div>
        </div>
      </div>
      <div className="space-y-4">
        {displayedCollaborators?.map((collaborator) => {
          const trustIndicator = getTrustIndicator(collaborator?.trustScore);
          
          return (
            <div key={collaborator?.id} className="p-3 border border-border rounded-lg hover:bg-muted/50 transition-micro">
              <div className="flex items-center space-x-3">
                {/* Avatar */}
                <div className="relative">
                  <img
                    src={collaborator?.avatar}
                    alt={`${collaborator?.name}'s profile`}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                  {/* Trust Score Indicator */}
                  <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-background border border-border rounded-full flex items-center justify-center">
                    <div className={`w-2 h-2 rounded-full ${
                      collaborator?.trustScore >= 90 ? 'bg-green-500' :
                      collaborator?.trustScore >= 80 ? 'bg-blue-500' :
                      collaborator?.trustScore >= 70 ? 'bg-yellow-500' : 'bg-red-500'
                    }`}></div>
                  </div>
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <h4 className="text-sm font-medium text-foreground truncate">
                      {collaborator?.name}
                    </h4>
                    {getRelationshipBadge(collaborator?.relationshipType)}
                  </div>
                  
                  <div className="flex items-center space-x-3 text-xs text-muted-foreground">
                    <span>{collaborator?.expenseCount} expenses</span>
                    <span>•</span>
                    <span>${collaborator?.totalAmount?.toFixed(2)} total</span>
                    <span>•</span>
                    <span className={trustIndicator?.color}>
                      {trustIndicator?.label} trust
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between mt-2">
                    <div className="flex items-center space-x-2 text-xs text-muted-foreground">
                      <Icon name="Users" size={12} />
                      <span>{collaborator?.mutualFriends} mutual friends</span>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Last: {new Date(collaborator?.lastActivity)?.toLocaleDateString()}
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex flex-col space-y-1">
                  <button
                    onClick={() => console.log('View profile:', collaborator?.name)}
                    className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted rounded transition-micro"
                    title="View Profile"
                  >
                    <Icon name="User" size={14} />
                  </button>
                  <button
                    onClick={() => console.log('Send message:', collaborator?.name)}
                    className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted rounded transition-micro"
                    title="Send Message"
                  >
                    <Icon name="MessageCircle" size={14} />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {/* Show More/Less Button */}
      {collaborators?.length > 3 && (
        <div className="mt-4 text-center">
          <button
            onClick={() => setShowAll(!showAll)}
            className="text-sm text-primary hover:text-primary/80 font-medium transition-micro"
          >
            {showAll ? (
              <span className="flex items-center space-x-1">
                <Icon name="ChevronUp" size={16} />
                <span>Show Less</span>
              </span>
            ) : (
              <span className="flex items-center space-x-1">
                <Icon name="ChevronDown" size={16} />
                <span>Show {collaborators?.length - 3} More</span>
              </span>
            )}
          </button>
        </div>
      )}
      {/* Footer Actions */}
      <div className="mt-6 pt-4 border-t border-border">
        <div className="flex items-center justify-between">
          <div className="text-xs text-muted-foreground">
            Privacy respecting • Only shows mutual interactions
          </div>
          <Button variant="outline" size="sm" iconName="Users" iconPosition="left">
            Find Friends
          </Button>
        </div>
      </div>
    </div>
  );
};

export default FrequentCollaborators;
